import useExtractMaterialPurchaseDataFromReceiptDocument from '@/api/mutations/materialsList/useExtractMaterialPurchaseDataFromReceiptDocument';
import useRecordMaterialPurchasesFromReceiptDocument from '@/api/mutations/materialsList/useRecordMaterialPurchasesFromReceiptDocument';
import useGetUpload from '@/api/queries/files/useGetUpload';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import useGetMyTaxRates from '@/api/queries/me/useGetMyTaxRates';
import { RecordMaterialPurchasesFromReceiptDocumentInput } from '@/gql/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Badge,
  Button,
  Card,
  CurrencyInput,
  Field,
  Form,
  RawInput,
  Select,
  Sidebar,
  SidebarProps,
  SkeletonLoader,
  useToastContext,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import AppearTransition from '@payaca/components/transitions/AppearTransition';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FieldArrayWithId,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod/v4';

const FORM_SCHEMA = z.object({
  materialsListId: z.string(),
  receiptDocumentUploadId: z.string(),
  supplier: z
    .object({
      new: z.object({
        name: z.string().min(1, 'Supplier name is required'),
      }),
      existing: z.object({
        id: z.string().min(1, 'Supplier ID is required'),
      }),
    })
    .partial()
    .refine(
      (data) => !!data.existing || !!data.new,
      'Either an existing supplier or a new supplier must be provided'
    ),
  materialPurchases: z.array(
    z.object({
      quantity: z
        .number()
        .positive('Quantity must be greater than or equal to 0'),
      unitPrice: z.number().positive('Unit price must be a positive number'),
      taxPercentage: z
        .number()
        .nonnegative('Tax percentage must be a positive number')
        .max(100, 'Tax percentage must not exceed 100%'),
      material: z
        .object({
          new: z.object({
            name: z.string().min(1, 'Material name is required'),
            description: z.string().optional(),
          }),
          existing: z.object({
            id: z.string().min(1, 'Material ID is required'),
          }),
        })
        .partial()
        .refine(
          (data) => !!data.existing || !!data.new,
          'Either an existing material or a new material must be provided'
        ),
    })
  ),
});

type TFormState = z.infer<typeof FORM_SCHEMA>;

export const ImportMaterialPurchasesFromReceiptDocumentUploadSidebar: FC<
  Omit<SidebarProps, 'size' | 'title' | 'behind'> & {
    uploadId: string;
    materialsListId: string;
    onSuccess: () => void;
  }
> = (props) => {
  const { uploadId, materialsListId, isOpen, onClose, onSuccess, ...rest } =
    props;

  const {
    mutate: mutateExtractMaterialPurchaseDataFromReceiptDocument,
    data: materialPurchaseDataFromReceiptDocument,
    isPending: isExtracting,
    isError: isExtractError,
  } = useExtractMaterialPurchaseDataFromReceiptDocument();

  const {
    mutate: recordMaterialPurchasesFromReceiptDocument,
    isPending: isRecording,
  } = useRecordMaterialPurchasesFromReceiptDocument({
    onError: (error) => {
      pushError('Something went wrong.');
    },
    onSuccess: () => {
      onSuccess();
    },
  });

  const somethingWentWrong =
    isExtractError ||
    (materialPurchaseDataFromReceiptDocument &&
      !materialPurchaseDataFromReceiptDocument
        .extractMaterialPurchaseDataFromReceiptDocument.materialPurchases
        .length);

  const { upload } = useGetUpload(uploadId);

  const { pushError } = useToastContext();

  useEffect(() => {
    if (uploadId) {
      mutateExtractMaterialPurchaseDataFromReceiptDocument({
        receiptDocumentUploadId: uploadId,
      });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'mutateExtractMaterialPurchaseDataFromReceiptDocument'
  }, [uploadId]);

  const recordMaterialPurchasesFromReceiptDocumentInput = useMemo(() => {
    const data =
      materialPurchaseDataFromReceiptDocument?.extractMaterialPurchaseDataFromReceiptDocument;
    if (data) {
      let supplier = undefined;

      if (data.supplier?.new) {
        supplier = {
          new: { name: data.supplier.new.name },
        };
      } else if (data.supplier?.existing) {
        supplier = {
          existing: { id: data.supplier.existing.id },
        };
      }

      return {
        receiptDocumentUploadId: uploadId,
        materialsListId: materialsListId,
        supplier,
        materialPurchases:
          materialPurchaseDataFromReceiptDocument.extractMaterialPurchaseDataFromReceiptDocument.materialPurchases.map(
            (materialPurchase) => {
              return {
                material: {
                  new: {
                    name: materialPurchase.material?.new?.name || '',
                  },
                },
                unitPrice: materialPurchase.unitPrice.value,
                quantity: materialPurchase.quantity,
                taxPercentage: materialPurchase.taxPercentage,
              };
            }
          ),
      };
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'uploadId', and 'materialsListId'
  }, [
    materialPurchaseDataFromReceiptDocument?.extractMaterialPurchaseDataFromReceiptDocument,
  ]);

  const handleRecordPurchases = (data: TFormState) => {
    recordMaterialPurchasesFromReceiptDocument(
      data as unknown as RecordMaterialPurchasesFromReceiptDocumentInput
    );
  };

  const formMethods = useForm<TFormState>({
    defaultValues: recordMaterialPurchasesFromReceiptDocumentInput,
    resolver: zodResolver(FORM_SCHEMA),
  });

  useEffect(() => {
    formMethods.reset(recordMaterialPurchasesFromReceiptDocumentInput);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'formMethods'
  }, [recordMaterialPurchasesFromReceiptDocumentInput]);

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        title="Importing Material purchases from Invoice"
        onClose={onClose}
        {...rest}
        warnUserOnClose={true}
        warnUserContent={{
          body: `Closing the sidebar will discard any un-imported Material purchases.`,
          confirm: 'Close',
        }}
      >
        <Sidebar.Body>
          <Form
            className="h-full flex flex-col"
            methods={formMethods}
            onSubmit={handleRecordPurchases}
            onError={console.error}
          >
            <div className="h-full flex flex-col overflow-hidden">
              <div className="p-4 grow shrink overflow-y-scroll">
                <div className="mb-8">
                  <Badge colour="blue" variant="soft">
                    Beta feature
                  </Badge>
                </div>
                {upload?.thumbnailUrl && (
                  <div className="flex items-center justify-center mb-8">
                    <img
                      className="max-w-[150px] max-h-[150px] h-auto rounded-lg object-cover"
                      src={upload?.thumbnailUrl}
                      alt={upload?.fileName}
                    />
                  </div>
                )}
                {somethingWentWrong && (
                  <>
                    <h4 className="mb-4">Something went wrong</h4>
                    <p>
                      We were not able to extract any data from the uploaded
                      document.
                    </p>
                  </>
                )}
                {isExtracting && (
                  <div className="space-y-6">
                    <p>
                      {' '}
                      <UntitledIcon name="star-06.3" className="h-4 w-4" />{' '}
                      Extracting data from Invoice...
                    </p>
                    <SkeletonLoader.Title />
                    <SkeletonLoader.Title />
                    <div className="space-y-4">
                      <SkeletonLoaderExtractedMaterialPurchaseCard />
                      <SkeletonLoaderExtractedMaterialPurchaseCard />
                      <SkeletonLoaderExtractedMaterialPurchaseCard />
                    </div>
                  </div>
                )}
                {!somethingWentWrong &&
                  materialPurchaseDataFromReceiptDocument && (
                    <>
                      {recordMaterialPurchasesFromReceiptDocumentInput && (
                        <ExtractedMaterialPurchaseData
                          supplierName={
                            materialPurchaseDataFromReceiptDocument
                              .extractMaterialPurchaseDataFromReceiptDocument
                              ?.supplier?.new?.name ||
                            materialPurchaseDataFromReceiptDocument
                              .extractMaterialPurchaseDataFromReceiptDocument
                              ?.supplier?.existing?.name ||
                            ''
                          }
                        />
                      )}
                    </>
                  )}
              </div>
              {!somethingWentWrong && (
                <div className="mt-auto p-4 space-y-4">
                  <Button
                    className="w-full"
                    type="submit"
                    isProcessing={isRecording}
                  >
                    Import
                  </Button>
                </div>
              )}
            </div>
          </Form>
        </Sidebar.Body>
      </Sidebar>
    </>
  );
};

const SkeletonLoaderExtractedMaterialPurchaseCard: FC = () => {
  return (
    <Card>
      <Card.Body className="flex flex-col gap-y-4">
        <div className="grow">
          <SkeletonLoader.Title className="w-3/4" />
        </div>
        <div className="flex flex-row gap-x-4">
          <div className="w-full">
            <SkeletonLoader.Input />
          </div>
          <div className="w-[150px]">
            <SkeletonLoader.Input />
          </div>
          <div className="w-[150px]">
            <SkeletonLoader.Input />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const ExtractedMaterialPurchaseData: FC<{
  supplierName: string;
}> = ({ supplierName }) => {
  const { data: regionPreferences } = useGetMyRegionPreferences();

  const formMethods = useFormContext<TFormState>();

  const { control } = formMethods;

  const { fields, remove } = useFieldArray({
    name: 'materialPurchases',
    control,
    shouldUnregister: true,
  });

  const materialPurchases = useWatch({
    control,
    name: 'materialPurchases',
  });

  const totals = materialPurchases?.reduce(
    (acc, field) => {
      const unitPrice = field.unitPrice || 0;
      const quantity = field.quantity || 0;
      const taxPercentage = field.taxPercentage || 0;

      const subtotal = unitPrice * quantity;
      const taxTotal = (subtotal * taxPercentage) / 100;
      const total = subtotal + taxTotal;

      acc.subtotal += Math.round(subtotal);
      acc.taxTotal += Math.round(taxTotal);
      acc.total += Math.round(total);

      return acc;
    },
    {
      subtotal: 0,
      taxTotal: 0,
      total: 0,
    }
  );

  return (
    <div>
      <div className="space-y-2">
        <h3>Supplier: {supplierName}</h3>
        <h3>Materials purchased</h3>
        <div className="space-y-4">
          {fields.map((field, index) => {
            return (
              <ExtractedMaterialPurchaseCard
                key={field.id}
                index={index}
                field={field}
                onRemove={() => remove(index)}
              />
            );
          })}
          {regionPreferences && totals && (
            <Card>
              <Card.Body>
                <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-right">
                  <div className="font-semibold">Subtotal:</div>
                  <div>
                    <CurrencyValue
                      value={totals.subtotal}
                      currency={regionPreferences.currency}
                    />
                  </div>
                  <div className="font-semibold">Tax:</div>
                  <div>
                    <CurrencyValue
                      value={totals.taxTotal}
                      currency={regionPreferences.currency}
                    />
                  </div>
                  <div className="font-semibold">Total:</div>
                  <div>
                    <CurrencyValue
                      value={totals.total}
                      currency={regionPreferences.currency}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const ExtractedMaterialPurchaseCard: FC<{
  onRemove: () => void;
  index: number;
  field: FieldArrayWithId<TFormState, 'materialPurchases'>;
}> = ({ index, onRemove, field }) => {
  const [removing, setRemoving] = useState(false);

  const formMethods = useFormContext<TFormState>();
  const { control, register } = formMethods;

  const { data: regionPreferences } = useGetMyRegionPreferences();
  const { taxRates, isLoading: isLoadingTaxRates } = useGetMyTaxRates();

  const taxRateOptions = [
    ...new Set(
      taxRates
        ?.filter((taxRate) => !taxRate.isReverseCharge && taxRate.isIncluded)
        .map((taxRate) => taxRate.percentage) || []
    ),
  ].map((percentage) => ({
    label: `${percentage}%`,
    value: percentage,
  }));

  const unitPriceName = `materialPurchases.${index}.unitPrice` as const;
  const quantityName = `materialPurchases.${index}.quantity` as const;
  const taxPercentageName = `materialPurchases.${index}.taxPercentage` as const;

  return (
    <AppearTransition
      appear={false}
      key={field.id}
      show={!removing}
      afterLeave={onRemove}
    >
      <Card>
        <Card.Body>
          <div className="flex justify-between gap-2 mb-2">
            <h4>{field.material.new?.name}</h4>
            <Button
              variant="white"
              size="xs"
              className="shrink-0"
              onClick={() => {
                setRemoving(true);
              }}
            >
              Remove
            </Button>
          </div>
          <div className="flex flex-row gap-x-4">
            <Field name={unitPriceName} className="basis-full">
              <Field.Label>Unit Price</Field.Label>
              <Controller
                name={unitPriceName}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CurrencyInput
                    value={value}
                    onChange={onChange}
                    currency={regionPreferences?.currency}
                  />
                )}
              />
            </Field>
            <Field name={quantityName} className="basis-[150px]">
              <Field.Label>Quantity</Field.Label>
              <RawInput {...register(quantityName, { valueAsNumber: true })} />
            </Field>
            <Field name={taxPercentageName} className="basis-[150px]">
              <Field.Label>Tax</Field.Label>
              <Controller
                name={taxPercentageName}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={taxRateOptions}
                    value={value}
                    onChange={onChange}
                    disabled={isLoadingTaxRates}
                  />
                )}
              />
            </Field>
          </div>
        </Card.Body>
      </Card>
    </AppearTransition>
  );
};
